import { RefObject, useEffect } from "react";

/**
 * Stacked-sheet scrolling. Every direct child of `container` with the class
 * `is-stack__item` is `position: sticky` (see motion.css). For each one we set
 * `--is-stack-top` = min(0, viewport height − its height), so a section taller
 * than the screen scrolls all the way through first and only then pins —
 * nothing gets hidden — while the next section slides up over it.
 *
 * A sheet that follows a tall one gets `--is-stack-dwell` (motion.css) as a
 * top margin: the gap is transparent, so the tall sheet's bottom (e.g. the
 * scope bar under Services) stays fully on screen for a beat before the next
 * sheet starts covering it.
 */
export function useStackedSections(container: RefObject<HTMLElement>) {
  useEffect(() => {
    const root = container.current;
    if (!root) return;
    const items: HTMLElement[] = Array.from(
      root.querySelectorAll<HTMLElement>(":scope > .is-stack__item")
    );

    const update = () => {
      const vh = window.innerHeight;
      // Pin below the sticky header so nothing ends up hidden behind it.
      // A sheet too tall to fit under the header pins at vh − height instead
      // (its top has already scrolled off-screen by then; full-screen sheets
      // pin at 0 and clear the header with their own top padding).
      const header = document.querySelector<HTMLElement>(".is-header");
      const offset = header ? header.offsetHeight : 0;
      items.forEach((el, i) => {
        el.style.setProperty("--is-stack-top", `${Math.min(offset, vh - el.offsetHeight)}px`);
        const prev = items[i - 1];
        el.classList.toggle("is-stack__item--after-tall", !!prev && prev.offsetHeight > vh);
      });
    };

    update();
    const ro = new ResizeObserver(update);
    items.forEach((el) => ro.observe(el));
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [container]);
}
